import { useEffect, useRef, useState } from "react";

interface Feedback {
  ok: boolean;
  text: string;
}

interface BarcodeScanInputProps {
  onScan: (value: string) => void;
  busy?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  feedback?: Feedback | null;
}

/**
 * Reusable barcode/SKU scan input. Treats common scanners as keyboard input
 * terminated by Enter, re-focuses after each scan, and shows immediate feedback.
 */
export function BarcodeScanInput({ onScan, busy, placeholder, autoFocus = true, feedback }: BarcodeScanInputProps) {
  const [value, setValue] = useState("");
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  const submit = () => {
    const v = value.trim();
    if (!v || busy) return;
    onScan(v);
    setValue("");
    ref.current?.focus();
  };

  return (
    <div>
      <div className="flex items-center gap-2">
        <input
          ref={ref}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
          aria-label="Scan barcode or SKU"
          placeholder={placeholder ?? "Scan barcode or SKU, then press Enter"}
          className="flex-1 min-h-12 px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-base md:text-lg font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
        />
        <button
          onClick={submit}
          disabled={busy || !value.trim()}
          className="px-5 py-3 min-h-12 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-base font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
        >
          <i className="ri-scan-line" aria-hidden="true" /> Scan
        </button>
      </div>
      {feedback && (
        <div
          role="status"
          aria-live="polite"
          className={`mt-2 px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${
            feedback.ok ? "bg-emerald-50 border border-emerald-100 text-emerald-700" : "bg-red-50 border border-red-100 text-red-700"
          }`}
        >
          <i className={feedback.ok ? "ri-checkbox-circle-line" : "ri-error-warning-line"} aria-hidden="true" />
          {feedback.text}
        </div>
      )}
    </div>
  );
}